//#region ../../shared/hash.ts
const MAX_32BIT_INT = 2147483647;
const HASH_LENGTH = 5;
const simpleHash = (input) => {
	const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	let hash = 0;
	for (let i = 0; i < input.length; i++) hash = (hash << HASH_LENGTH) + hash + input.charCodeAt(i) & MAX_32BIT_INT;
	let result = "";
	for (let i = 0; i < HASH_LENGTH; i++) {
		result += chars[hash % 62];
		hash = Math.floor(hash / 62);
	}
	return result;
};

//#endregion
//#region src/core.ts
const DEFAULT_NAMESPACE = "quick";
const MAX_SNAPSHOT_HISTORY = 128;
const VALID_PLURAL_FORMS = new Set([
	"zero",
	"one",
	"two",
	"few",
	"many",
	"other"
]);
const buildTemplate = (values, args) => {
	let template = "";
	let fallbackText = "";
	const variables = {};
	for (let index = 0; index < values.length; index += 1) {
		const chunk = values[index];
		template += chunk;
		fallbackText += chunk;
		if (index < args.length) {
			var _args$index;
			template += `{{${index}}}`;
			fallbackText += String((_args$index = args[index]) !== null && _args$index !== void 0 ? _args$index : "");
			variables[String(index)] = args[index];
		}
	}
	return {
		template,
		fallbackText,
		variables
	};
};
const createTq = (options) => {
	var _options$namespace, _options$hashFn, _options$treatReturne, _options$onMissing, _options$debug, _options$resolveLocal;
	const namespace = (_options$namespace = options.namespace) !== null && _options$namespace !== void 0 ? _options$namespace : DEFAULT_NAMESPACE;
	const hashFn = (_options$hashFn = options.hashFn) !== null && _options$hashFn !== void 0 ? _options$hashFn : simpleHash;
	const treatReturnedKeyAsMissing = (_options$treatReturne = options.treatReturnedKeyAsMissing) !== null && _options$treatReturne !== void 0 ? _options$treatReturne : true;
	const onMissing = (_options$onMissing = options.onMissing) !== null && _options$onMissing !== void 0 ? _options$onMissing : (() => {});
	const debug = (_options$debug = options.debug) !== null && _options$debug !== void 0 ? _options$debug : (() => {});
	const resolveLocale = (_options$resolveLocal = options.resolveLocale) !== null && _options$resolveLocal !== void 0 ? _options$resolveLocal : (() => options.locale);
	const pluralRulesCache = /* @__PURE__ */ new Map();
	const getPluralRules = (locale) => {
		const cacheKey = locale;
		const cached = pluralRulesCache.get(cacheKey);
		if (cached) return cached;
		let rules;
		try {
			rules = locale ? new Intl.PluralRules(locale) : new Intl.PluralRules();
		} catch (_unused) {
			rules = new Intl.PluralRules();
		}
		pluralRulesCache.set(cacheKey, rules);
		return rules;
	};
	const snapshots = [];
	const storeSnapshot = (snapshot) => {
		snapshots.push(snapshot);
		if (snapshots.length > MAX_SNAPSHOT_HISTORY) snapshots.shift();
	};
	const takeSnapshotForOutput = (output) => {
		if (typeof output !== "string") return;
		for (let index = snapshots.length - 1; index >= 0; index -= 1) {
			const entry = snapshots[index];
			if (entry.output === output) {
				snapshots.splice(index, 1);
				return entry;
			}
		}
	};
	const invokeTranslate = (key, info) => {
		const translated = options.translate(key, info);
		if (translated == null || treatReturnedKeyAsMissing && translated === key) {
			onMissing({
				key,
				info
			});
			storeSnapshot({
				key,
				info,
				output: key
			});
			return key;
		}
		storeSnapshot({
			key,
			info,
			output: translated
		});
		return translated;
	};
	const tq = (values, ...args) => {
		const info = buildTemplate(values, args);
		return invokeTranslate(`${namespace}.${hashFn(info.template)}`, info);
	};
	const plural = (count, forms, optionsOverride) => {
		var _ref, _canonicalForms$find, _optionsOverride$vari;
		const entries = Object.entries(forms);
		if (!entries.length) throw new Error("tq.plural requires at least one plural form");
		const canonicalForms = entries.map(([formKey, renderedValue]) => {
			if (!VALID_PLURAL_FORMS.has(formKey)) throw new Error(`tq.plural form "${formKey}" is not a valid CLDR plural category. Valid forms are: ${Array.from(VALID_PLURAL_FORMS).join(", ")}`);
			const snapshot = takeSnapshotForOutput(renderedValue);
			if (snapshot) return {
				formKey,
				template: snapshot.info.template,
				fallbackText: renderedValue,
				variables: snapshot.info.variables
			};
			return {
				formKey,
				template: renderedValue,
				fallbackText: renderedValue,
				variables: {}
			};
		});
		const hashInput = `plural|${[...canonicalForms].sort((a, b) => a.formKey.localeCompare(b.formKey)).map(({ formKey, template }) => `${formKey}:${template}`).join("|")}`;
		const baseKey = `${namespace}.${hashFn(hashInput)}`;
		const targetCategory = getPluralRules(resolveLocale()).select(count);
		const preferredForm = (_ref = (_canonicalForms$find = canonicalForms.find(({ formKey }) => formKey === targetCategory)) !== null && _canonicalForms$find !== void 0 ? _canonicalForms$find : canonicalForms.find(({ formKey }) => formKey === "other")) !== null && _ref !== void 0 ? _ref : canonicalForms[canonicalForms.length - 1];
		const fallbackText = forms[preferredForm.formKey];
		const info = {
			template: preferredForm.template,
			fallbackText,
			variables: {
				...preferredForm.variables,
				...(_optionsOverride$vari = optionsOverride === null || optionsOverride === void 0 ? void 0 : optionsOverride.variables) !== null && _optionsOverride$vari !== void 0 ? _optionsOverride$vari : {},
				count
			}
		};
		const suffixedKey = `${baseKey}_${preferredForm.formKey}`;
		debug({
			stage: "plural",
			key: suffixedKey,
			hashInput,
			info
		});
		return invokeTranslate(suffixedKey, info);
	};
	tq.plural = plural;
	return tq;
};
const interpolateQuickTemplate = (info) => info.template.replace(/\{\{(\d+)}}/g, (_, index) => {
	var _index;
	return String((_index = info.variables[index]) !== null && _index !== void 0 ? _index : "");
});

//#endregion
//#region src/i18next.ts
const DEFAULT_I18NEXT_TQ_OPTIONS = {
	namespace: "quick",
	baseOptions: {},
	onMissing: () => {},
	debug: () => {},
	locale: void 0,
	resolveLocale: void 0,
	i18n: void 0
};
const toI18nextTranslate = (t, options) => {
	return (key, info) => {
		const mergedOptions = {
			...options.baseOptions,
			...info.variables
		};
		return t(key, info.fallbackText, mergedOptions);
	};
};
const createI18nextTq = (t, options = {}) => {
	var _mergedOptions$resolv, _mergedOptions$locale;
	const mergedOptions = {
		...DEFAULT_I18NEXT_TQ_OPTIONS,
		...options
	};
	const baseOptionsLocale = typeof mergedOptions.baseOptions.lng === "string" ? mergedOptions.baseOptions.lng : void 0;
	const resolveLocale = (_mergedOptions$resolv = mergedOptions.resolveLocale) !== null && _mergedOptions$resolv !== void 0 ? _mergedOptions$resolv : (() => {
		var _ref, _mergedOptions$i18n$l, _mergedOptions$i18n;
		return (_ref = (_mergedOptions$i18n$l = (_mergedOptions$i18n = mergedOptions.i18n) === null || _mergedOptions$i18n === void 0 ? void 0 : _mergedOptions$i18n.language) !== null && _mergedOptions$i18n$l !== void 0 ? _mergedOptions$i18n$l : mergedOptions.locale) !== null && _ref !== void 0 ? _ref : baseOptionsLocale;
	});
	const staticLocale = (_mergedOptions$locale = mergedOptions.locale) !== null && _mergedOptions$locale !== void 0 ? _mergedOptions$locale : baseOptionsLocale;
	if (!resolveLocale()) throw new Error("createI18nextTq: Unable to determine locale. Provide `locale`, `baseOptions.lng`, `resolveLocale`, or pass an i18next instance via options.i18n.");
	return createTq({
		translate: toI18nextTranslate(t, mergedOptions),
		namespace: mergedOptions.namespace,
		onMissing: mergedOptions.onMissing,
		locale: staticLocale,
		resolveLocale,
		treatReturnedKeyAsMissing: false,
		debug: mergedOptions.debug
	});
};

//#endregion
export { DEFAULT_I18NEXT_TQ_OPTIONS, createI18nextTq, createTq, interpolateQuickTemplate };
//# sourceMappingURL=index.js.map